import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import type { LeaseRow } from '../rfpTypes';

interface FileUploadProps {
    onLeasesParsed: (rows: LeaseRow[]) => void;
}

const REQUIRED_COLUMNS = [
    'lease_name',
    'avg_monthly_production_bbl',
    'avg_bsw',
    'avg_gravity',
    'latitude',
    'longitude',
] as const;

const OPTIONAL_COLUMNS = ['region'] as const;

function downloadTemplate() {
    const headers = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];
    const exampleRow = {
        lease_name: 'Example Lease 1',
        avg_monthly_production_bbl: 5000,
        avg_bsw: 0.5,
        avg_gravity: 42.0,
        latitude: 39.1234,
        longitude: -97.5678,
        region: 'North',
    };
    const ws = XLSX.utils.json_to_sheet([exampleRow], { header: headers });

    // Style header row width hints (XLSX doesn't support cell styles in utils, but we can set col widths)
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length + 2, 18) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leases');
    XLSX.writeFile(wb, 'rfp_lease_template.xlsx');
}

function normalizeHeader(h: string): string {
    return h.trim().toLowerCase().replace(/\s+/g, '_');
}

export default function FileUpload({ onLeasesParsed }: FileUploadProps) {
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);

    const parseFile = useCallback((file: File) => {
        setError(null);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target!.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const raw: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

                if (!raw.length) {
                    setError('The file appears to be empty.');
                    return;
                }

                // Normalize column headers
                const normalized = raw.map((row) => {
                    const out: Record<string, unknown> = {};
                    for (const key of Object.keys(row)) {
                        out[normalizeHeader(key)] = row[key];
                    }
                    return out;
                });

                // Validate required columns
                const firstRow = normalized[0];
                const missing = REQUIRED_COLUMNS.filter((col) => !(col in firstRow));
                if (missing.length) {
                    setError(`Missing required columns: ${missing.join(', ')}`);
                    return;
                }

                // Build typed rows
                const leaseRows: LeaseRow[] = normalized.map((row, i) => {
                    const n = (field: string) => {
                        const val = Number(row[field]);
                        if (isNaN(val)) {
                            throw new Error(
                                `Row ${i + 2}: "${field}" must be a number, got "${row[field]}"`
                            );
                        }
                        return val;
                    };

                    return {
                        lease_name:                String(row['lease_name'] ?? '').trim(),
                        avg_monthly_production_bbl: n('avg_monthly_production_bbl'),
                        avg_bsw:                   n('avg_bsw'),
                        avg_gravity:               n('avg_gravity'),
                        latitude:                  n('latitude'),
                        longitude:                 n('longitude'),
                        region:                    row['region'] ? String(row['region']).trim() : undefined,
                    };
                });

                setFileName(file.name);
                onLeasesParsed(leaseRows);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Failed to parse file.');
            }
        };

        reader.readAsArrayBuffer(file);
    }, [onLeasesParsed]);

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length) parseFile(accepted[0]);
        },
        [parseFile]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        multiple: false,
    });

    return (
        <div className="rfp-fileUpload">
            <div
                {...getRootProps()}
                style={{
                    border: `2px dashed ${isDragActive ? '#3b82f6' : '#6b7280'}`,
                    borderRadius: 8,
                    padding: '24px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: isDragActive ? '#eff6ff' : '#f9fafb',
                    transition: 'all 0.15s',
                }}
            >
                <input {...getInputProps()} />
                {fileName ? (
                    <p style={{ margin: 0, color: '#374151', fontWeight: 500 }}>
                        ✓ {fileName}
                        <span
                            style={{ marginLeft: 10, fontSize: 12, color: '#6b7280', fontWeight: 400 }}
                        >
                            (drop or click to replace)
                        </span>
                    </p>
                ) : isDragActive ? (
                    <p style={{ margin: 0, color: '#3b82f6' }}>Drop the file here…</p>
                ) : (
                    <p style={{ margin: 0, color: '#6b7280' }}>
                        Drag & drop a <strong>.csv</strong> or <strong>.xlsx</strong> file, or{' '}
                        <span style={{ color: '#3b82f6', textDecoration: 'underline' }}>browse</span>
                    </p>
                )}
            </div>

            {error && (
                <p style={{ marginTop: 8, color: '#dc2626', fontSize: 13 }}>{error}</p>
            )}

            <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
                Required columns:{' '}
                {REQUIRED_COLUMNS.map((c) => (
                    <code key={c} style={{ marginRight: 4 }}>
                        {c}
                    </code>
                ))}
                &nbsp;· Optional:{' '}
                {OPTIONAL_COLUMNS.map((c) => (
                    <code key={c} style={{ marginRight: 4 }}>
                        {c}
                    </code>
                ))}
            </div>

            <button
                onClick={downloadTemplate}
                style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#2563eb',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                }}
            >
                ↓ Download template (.xlsx)
            </button>
        </div>
    );
}
